import React from 'react';
import styled, { keyframes } from 'styled-components';

import { Logo } from 'shared/assets/logo';
import { mediaQueries } from 'shared/styles';

import { BaseSlideContent, BaseSlideWrapper, BaseSlideCta } from './login.styles';

const WAVE_BARS = [
  { id: 'w0', delay: '0s' },
  { id: 'w1', delay: '0.1s' },
  { id: 'w2', delay: '0.2s' },
  { id: 'w3', delay: '0.3s' },
  { id: 'w4', delay: '0.15s' },
  { id: 'w5', delay: '0.25s' },
  { id: 'w6', delay: '0.05s' },
  { id: 'w7', delay: '0.35s' },
];

export const ConsumerAI = (): JSX.Element => (
  <Wrapper>
    <Logo fill='#F0E7D4' />
    <Content>
      <Kicker>
        <KickerDot />
        New &middot; Consumer AI Suite
      </Kicker>
      <Headline>
        Catch every call,
        <br />
        chat, cart &amp; review.
        <br />
        <em>One AI.</em>
      </Headline>

      <Cta
        aria-label='Explore Consumer AI — opens in a new tab'
        href='https://dutchie.com/business/consumer-ai-demo?utm_source=backoffice&utm_medium=banner&utm_campaign=consumerailaunchjun26'
        rel='noreferrer'
        target='_blank'
      >
        Explore Consumer AI
      </Cta>

      <CardsGrid>
        {/* Voice AI */}
        <Card $accent='rgba(227,184,76,0.30)' $glow='rgba(227,184,76,0.08)' $n={1}>
          <CardHeader>
            <CardTag $color='#E3B84C'>
              <Pulse $color='#E3B84C' />
              Voice AI
            </CardTag>
            <CardMeta>0:42</CardMeta>
          </CardHeader>
          <CardName>Inbound call &middot; +1 (303) 555&hellip;</CardName>
          <CardLine>&ldquo;Do you have Blue Dream in stock?&rdquo;</CardLine>
          <WaveRow>
            {WAVE_BARS.map((b) => (
              <WaveBar key={b.id} $delay={b.delay} />
            ))}
          </WaveRow>
        </Card>

        {/* Agentic Commerce */}
        <Card $accent='rgba(106,180,72,0.30)' $glow='rgba(106,180,72,0.08)' $n={2}>
          <CardHeader>
            <CardTag $color='#6AB448'>Agentic Commerce</CardTag>
            <CardMeta>Cart &middot; 3</CardMeta>
          </CardHeader>
          <CardItem>
            <span>Wyld &middot; Huckleberry</span>
            <CardPrice $color='#6AB448'>$24</CardPrice>
          </CardItem>
          <CardItem>
            <span>Camino &middot; Midnight</span>
            <CardPrice $color='#6AB448'>$22</CardPrice>
          </CardItem>
          <CardRow>
            <Chk>&#10003;</Chk>
            Reorder ready in one tap
          </CardRow>
        </Card>

        {/* Register Co-Pilot */}
        <Card $accent='rgba(227,184,76,0.30)' $glow='rgba(227,184,76,0.08)' $n={3}>
          <CardHeader>
            <CardTag $color='#E3B84C'>
              <Pulse $color='#E3B84C' />
              Register Co-Pilot
            </CardTag>
            <CardMeta>POS</CardMeta>
          </CardHeader>
          <CardLine>Suggested upsell for this basket</CardLine>
          <CardItem>
            <span>Pre-roll 5pk</span>
            <CardPrice $color='#E3B84C'>+$32</CardPrice>
          </CardItem>
          <CardRow>
            <Chk>&#10003;</Chk>
            Accepted by budtender
          </CardRow>
        </Card>

        {/* Consumer Pulse */}
        <Card $accent='rgba(139,111,230,0.32)' $glow='rgba(139,111,230,0.08)' $n={4}>
          <CardHeader>
            <CardTag $color='#8B6FE6'>
              <Pulse $color='#8B6FE6' />
              Consumer Pulse
            </CardTag>
            <CardMeta>Today</CardMeta>
          </CardHeader>
          <BigStat>
            +18<BigUnit>&nbsp;CSAT</BigUnit>
          </BigStat>
          <CardLineSpaced>
            Sentiment up across 12 locations &mdash; surfaced from reviews &amp; surveys.
          </CardLineSpaced>
        </Card>
      </CardsGrid>
    </Content>
  </Wrapper>
);

/* ── Animations ──────────────────────────────────────────────────────── */
const rise = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const float = keyframes`
  0%, 100% { transform: translate3d(0,0,0) rotateX(0deg); }
  50%       { transform: translate3d(0,-6px,8px) rotateX(1.2deg); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.8); }
`;
const wave = keyframes`
  0%, 100% { transform: scaleY(0.25); }
  50%       { transform: scaleY(1); }
`;

/* ── Layout ──────────────────────────────────────────────────────────── */
const Wrapper = styled(BaseSlideWrapper)`
  background: #062f22;
  padding-top: 58px;
  position: relative;
`;

const Content = styled(BaseSlideContent)`
  color: #f0e7d4;
  padding-top: 120px;
  width: 100%;
  padding-left: 52px;
  padding-right: 52px;
  align-items: flex-start;
`;

const Kicker = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #e3b84c;
  margin-bottom: 20px;
`;

const KickerDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e3b84c;
  box-shadow: 0 0 12px #e3b84c;
  flex-shrink: 0;
`;

const Headline = styled.h1`
  color: #faf8f4;
  font-size: clamp(28px, 2.8vw, 44px);
  font-weight: 200;
  line-height: 1.05;
  letter-spacing: -0.025em;
  margin-bottom: 24px;

  em {
    font-style: italic;
    color: #e3b84c;
    font-weight: 200;
  }

  @media ${mediaQueries.belowMedium} {
    font-size: 32px;
  }
`;

const Cta = styled(BaseSlideCta)`
  background: #e3b84c;
  color: #1b1305;
  font-size: 14px;
  margin-bottom: 28px;

  &:hover {
    background: #efc96a;
  }
`;

/* ── Cards ───────────────────────────────────────────────────────────── */
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  max-width: 440px;
`;

const FLOAT_DELAYS = ['1.9s', '2.6s', '2.2s', '3.1s'];

const Card = styled.div<{ $accent: string; $glow: string; $n: 1 | 2 | 3 | 4 }>`
  border-radius: 9px;
  background: linear-gradient(180deg, rgba(20, 30, 24, 0.92), rgba(12, 20, 16, 0.92));
  border: 1px solid ${(p) => p.$accent};
  padding: 13px 14px;
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.5), 0 0 60px ${(p) => p.$glow};
  opacity: 0;
  animation:
    ${rise} 1s cubic-bezier(0.22, 0.61, 0.36, 1) ${(p) => ['0.55s', '0.7s', '0.85s', '1s'][p.$n - 1]}
      forwards,
    ${float} 7.5s ease-in-out ${(p) => FLOAT_DELAYS[p.$n - 1]} infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    opacity: 1 !important;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CardTag = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 8px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${(p) => p.$color};
`;

const Pulse = styled.span<{ $color: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  box-shadow: 0 0 8px ${(p) => p.$color};
  flex-shrink: 0;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const CardMeta = styled.div`
  font-size: 9px;
  color: rgba(250, 248, 244, 0.4);
`;

const CardName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #faf8f4;
  margin-bottom: 3px;
`;

const CardLine = styled.div`
  font-size: 10px;
  color: rgba(250, 248, 244, 0.65);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const CardLineSpaced = styled(CardLine)`
  margin-top: 8px;
  margin-bottom: 0;
`;

const CardItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  color: #faf8f4;
  margin-bottom: 4px;
`;

const CardPrice = styled.span<{ $color: string }>`
  color: ${(p) => p.$color};
  font-weight: 500;
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: rgba(250, 248, 244, 0.65);
  margin-top: 2px;
`;

const Chk = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(106, 180, 72, 0.2);
  color: #6ab448;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
  flex-shrink: 0;
`;

const WaveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5px;
  height: 18px;
`;

const WaveBar = styled.span<{ $delay: string }>`
  display: block;
  width: 2px;
  height: 16px;
  border-radius: 1px;
  background: #e3b84c;
  opacity: 0.85;
  transform-origin: center;
  animation: ${wave} 1.1s ease-in-out ${(p) => p.$delay} infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: scaleY(0.5);
  }
`;

const BigStat = styled.div`
  font-weight: 200;
  font-size: 28px;
  letter-spacing: -0.02em;
  color: #faf8f4;
  line-height: 1;
  margin-bottom: 6px;
`;

const BigUnit = styled.span`
  color: #6ab448;
  font-size: 12px;
  font-weight: 500;
`;
